import { Timestamp } from "google-protobuf/google/protobuf/timestamp_pb";
import { EventVisibility as EventVisibilityProto } from "../generated/drinks_pb";
import {
    EventCategoryBreakdown,
    EventDrinkUserBreakdown,
} from "./drinks/utils/normalization";

export type DrinkOption = {
    id: number;
    name: string;
    promille: number;
};

export type DrinkActivitySummary = {
    activityId?: number;
    amountMl: number;
    drinkId: number | null;
    drinkName: string;
    promille: number;
    timestamp: Date | null;
    eventId: number | null;
    startedAt: Date | null;
    durationMinutes: number | null;
    /** Set on drinks still in the offline outbox (see usePending). */
    pending?: "queued" | "rejected";
    /** Owner of a pending drink, so a shared device does not list another account's queue. */
    pendingUserId?: number;
};

export const fromTimestamp = (timestamp?: Timestamp): Date | null => {
    if (!timestamp) {
        return null;
    }
    const millis = timestamp.getSeconds() * 1000 + timestamp.getNanos() / 1_000_000;
    return new Date(millis);
};

export const buildRecentTemplateKey = (drinkId: number, amountMl: number): string => {
    const normalizedAmount = Math.round(amountMl * 1000); // preserve up to 1/1000 ml
    return `${drinkId}:${normalizedAmount}`;
};

export type RecentDrinkTemplate = {
    templateKey: string;
    drinkId: number;
    drinkName: string;
    promille: number;
    amountMl: number;
    timestamp: Date;
    count: number;
    durationMinutes: number | null;
    /** Set on drinks still in the offline outbox (see usePending). */
    pending?: "queued" | "rejected";
};

export const getQuickDrinkName = (name: string): string =>
    name.replace(/\s+\d+(?:[.,]\d+)?\s*[mc]?l$/i, "");

export type DrinkActivityOptions = {
    durationMinutes?: number | null;
    startedAt?: Date | null;
    eventId?: number | null;
    idempotencyKey?: string;
};

export const deriveRecentDrinkTemplates = (
    activities: DrinkActivitySummary[],
    windowMs: number
): RecentDrinkTemplate[] => {
    const cutoff = Date.now() - windowMs;
    const sorted = [...activities]
        .filter((activity): activity is DrinkActivitySummary & { drinkId: number; timestamp: Date } =>
            Boolean(activity.drinkId && activity.timestamp && activity.timestamp.getTime() >= cutoff)
        )
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    const templates: Map<string, RecentDrinkTemplate> = new Map();
    sorted.forEach((activity) => {
        const templateKey = buildRecentTemplateKey(activity.drinkId, activity.amountMl);
        const existing = templates.get(templateKey);
        if (!existing) {
            templates.set(templateKey, {
                templateKey,
                drinkId: activity.drinkId,
                drinkName: activity.drinkName,
                promille: activity.promille,
                amountMl: activity.amountMl,
                timestamp: activity.timestamp,
                count: 1,
                durationMinutes: activity.durationMinutes ?? null,
            });
        } else {
            existing.count += 1;
            if (activity.durationMinutes != null) {
                existing.durationMinutes = activity.durationMinutes;
            }
        }
    });

    return Array.from(templates.values()).sort(
        (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );
};

type EventVisibility = EventVisibilityProto;

export type EventSummary = {
    eventId: number;
    eventName: string;
    location: string;
    start?: Date | null;
    end?: Date | null;
    ownerUserId: number;
    isActive: boolean;
    visibility: EventVisibility;
    requiresPassword: boolean;
    isParticipant: boolean;
};

// (…remaining types & functions omitted here for brevity — the pattern continues the same way)

export type EventTopConsumer = {
    userId: number;
    username: string;
    activityCount: number;
    totalVolumeMl: number;
    totalAlcoholGrams: number;
};

export type EventDrinkStatistic = {
    drinkId: number;
    drinkName: string;
    activityCount: number;
    totalVolumeMl: number;
    totalAlcoholGrams: number;
    topConsumer: EventTopConsumer | null;
    participants: EventTopConsumer[];
};

export type EventCategoryStatistic = {
    category: string;
    activityCount: number;
    totalVolumeMl: number;
    totalAlcoholGrams: number;
    topConsumer: EventTopConsumer | null;
    participants: EventTopConsumer[];
};

export type EventTimelineUserEntry = {
    userId: number;
    username: string;
    activityCount: number;
    totalVolumeMl: number;
    totalAlcoholGrams: number;
};

export type EventTimelineBucket = {
    bucketStart: Date;
    users: EventTimelineUserEntry[];
};

export type EventUserStatistics = {
    userId: number;
    username: string;
    activityCount: number;
    totalVolumeMl: number;
    totalAlcoholGrams: number;
    categories: EventCategoryBreakdown[];
    topDrinks: EventDrinkUserBreakdown[];
    allDrinks: EventDrinkUserBreakdown[];
};

export type EventStatistics = {
    eventId: number;
    eventName: string;
    start: Date | null;
    end: Date | null;
    participantCount: number;
    users: EventUserStatistics[];
    drinks: EventDrinkStatistic[];
    categories: EventCategoryStatistic[];
    timeline: EventTimelineBucket[];
};
